/* lib/adapters/ingredients.js — коды и форма данных «Сырья» между боевым API и UI прототипа.
 *
 * Главные расхождения (см. modules.md):
 * - category: бэк — slug ('meat'); UI прототипа группирует/фильтрует по RU-лейблу type_label.
 *   ВНИМАНИЕ: бэк type_label = ТИП КАРТОЧКИ ('Ингредиент'), НЕ еда-категория. Группируем по category.
 * - decimal-as-string: amount_rub, KBJU и пр. приходят строками → Number().
 * - price unit: бэк slug ('kg'); UI краткий RU ('кг'). Цена-юниты только kg/l/pcs/g.
 * - allergens: канон EU (14), 'Молоко (лактоза)' и т.п.
 */

// 17 категорий = ttk.constants.IngredientCategory (slug → RU label).
export const CATEGORY_SLUG_TO_LABEL = {
  grains: 'Крупы и злаки',
  vegetables: 'Овощи',
  fruits: 'Фрукты и ягоды',
  meat: 'Мясо и птица',
  fish: 'Рыба и морепродукты',
  dairy: 'Молочные продукты',
  eggs: 'Яйцепродукты',
  grocery: 'Бакалея',
  spices: 'Специи и приправы',
  sauces: 'Соусы и заправки',
  fats: 'Жиры и масла',
  beverages: 'Напитки',
  flour: 'Мука и выпечка',
  sweets: 'Кондитерские и сладкое',
  mushrooms: 'Грибы',
  herbs: 'Зелень',
  other: 'Прочее',
};
export const CATEGORY_LABEL_TO_SLUG = Object.fromEntries(
  Object.entries(CATEGORY_SLUG_TO_LABEL).map(([slug, label]) => [label, slug]),
);
// Порядок групп реестра + опции фильтра «Тип» (заменяет mock INGREDIENT_TYPES).
export const INGREDIENT_CATEGORIES = Object.values(CATEGORY_SLUG_TO_LABEL);

export function categoryLabel(slug) {
  return CATEGORY_SLUG_TO_LABEL[slug] || CATEGORY_SLUG_TO_LABEL.other;
}

// Юниты цены (slug ↔ краткий RU). Базовая единица карточки (default_unit) — уже RU ('кг'/'г'/'шт'/'л'/'мл').
export const PRICE_UNIT_SLUG_TO_RU = { kg: 'кг', l: 'л', pcs: 'шт', g: 'г' };
export const PRICE_UNIT_RU_TO_SLUG = { кг: 'kg', л: 'l', шт: 'pcs', г: 'g' };

// 14 канон. аллергенов EU = ttk.constants.EU_ALLERGENS.
export const EU_ALLERGENS = [
  'Глютен', 'Ракообразные', 'Яйца', 'Рыба', 'Арахис', 'Соя',
  'Молоко (лактоза)', 'Орехи', 'Сельдерей', 'Горчица',
  'Кунжут', 'Сульфиты', 'Люпин', 'Моллюски',
];

const num = (v) => (v == null || v === '' ? null : Number(v));

// Бэк current_price_brief {amount_rub:str, unit:slug, unit_label:'₽ / кг', supplier_name, rub_per_g}
// → форма реестра {amount_rub:Number, unit_label:'кг', supplier_name}.
export function fromApiPriceBrief(b) {
  if (!b) return null;
  return {
    amount_rub: num(b.amount_rub),
    unit_label: PRICE_UNIT_SLUG_TO_RU[b.unit] || b.unit,
    supplier_name: b.supplier_name || null,
  };
}

// Карточка из list/detail → форма карточки реестра. type_label := RU-категория (для группировки!).
export function fromApiCardBrief(c) {
  return {
    id: c.id,
    name: c.name,
    type_label: categoryLabel(c.category),
    current_price_brief: fromApiPriceBrief(c.current_price_brief),
  };
}

// ── Detail (редактор карточки) ────────────────────────────────────────
// current_price/price-row из _price_brief {id, amount_rub:str, unit:slug, effective_from, supplier_id, supplier_name, notes}
function fromApiPriceRow(p) {
  if (!p) return null;
  return {
    id: p.id,
    amount_rub: num(p.amount_rub),
    unit: PRICE_UNIT_SLUG_TO_RU[p.unit] || p.unit, // редактор показывает .../p.unit (RU)
    effective_from: p.effective_from,
    supplier_id: p.supplier_id ?? null,
    supplier_name: p.supplier_name || null,
    notes: p.notes || '',
  };
}

// detail (IngredientCardDetailSerializer) + used-in → форма mock-detail.
export function fromApiCardDetail(d, usedIn) {
  const usages = ((usedIn && usedIn.usages) || []).map((u) => ({
    ttk_id: u.ttk_id,
    ttk_title: u.ttk_title,
    ttk_kind_label: u.ttk_kind === 'semiproduct' ? 'Полуфабрикат' : (u.ttk_kind_label || 'Блюдо'),
    amount_netto_g: num(u.amount_netto_g),
  }));
  return {
    id: d.id,
    name: d.name,
    category: categoryLabel(d.category), // RU label (NativeSelect value)
    default_unit: d.default_unit || 'г',
    piece_weight_g: num(d.piece_weight_g),
    kcal_per_100g: num(d.kcal_per_100g),
    protein_per_100g: num(d.protein_per_100g),
    fat_per_100g: num(d.fat_per_100g),
    carbs_per_100g: num(d.carbs_per_100g),
    allergens: Array.isArray(d.allergens) ? d.allergens : [],
    notes: d.notes || '',
    current_price: fromApiPriceRow(d.current_price),
    prices: (d.prices || []).map(fromApiPriceRow),
    usages,
  };
}

// Пустой detail для создания (category — RU label).
export function blankCardDetail(prefill) {
  return {
    id: null,
    name: (prefill && prefill.name) || '',
    category: (prefill && prefill.category) || CATEGORY_SLUG_TO_LABEL.other,
    default_unit: 'г',
    piece_weight_g: null,
    kcal_per_100g: null, protein_per_100g: null, fat_per_100g: null, carbs_per_100g: null,
    allergens: [], notes: '',
    current_price: null, prices: [], usages: [],
  };
}

// card (UI) → payload IngredientCardWriteSerializer. category RU→slug; KBJU null опускаем
// (поля DecimalField required=False БЕЗ allow_null → null 400-нёт). prices только при создании.
export function toApiCardPayload(card, { withPrices = false } = {}) {
  const body = {
    name: (card.name || '').trim(),
    type: 'ingredient',
    category: CATEGORY_LABEL_TO_SLUG[card.category] || 'other',
    default_unit: card.default_unit || 'г',
    allergens: Array.isArray(card.allergens) ? card.allergens : [],
    notes: card.notes || '',
  };
  body.piece_weight_g = card.default_unit === 'шт' ? (card.piece_weight_g ?? null) : null;
  for (const k of ['kcal_per_100g', 'protein_per_100g', 'fat_per_100g', 'carbs_per_100g']) {
    if (card[k] != null && card[k] !== '') body[k] = Number(card[k]);
  }
  if (withPrices && Array.isArray(card.prices) && card.prices.length) {
    body.prices = card.prices
      .filter((p) => p && p.amount_rub != null)
      .map((p) => priceToApiPayload(p));
  }
  return body;
}

// price (UI, RU unit + supplier_name) → payload IngredientPriceWriteSerializer (supplier_id проставляется
// отдельно в facade через resolveSupplierId). unit RU→slug; неизвестная единица → kg по умолчанию.
export function priceToApiPayload(p) {
  return {
    amount_rub: Number(p.amount_rub),
    unit: PRICE_UNIT_RU_TO_SLUG[p.unit] || 'kg',
    effective_from: p.effective_from,
    notes: (p.notes || '').trim(),
  };
}
